import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Form, Input, Select, Switch, Checkbox, message } from "antd";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import { generateCodeFromName } from "../../../utils/codeUtils";

const { TextArea } = Input;
const { Option } = Select;

const ItemModal = ({
  visible,
  onCancel,
  onSuccess,
  item,
  categories,
  defaultCategory,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && item) {
      // Edit mode - populate form
      form.setFieldsValue({
        category: item.category,
        name: item.name,
        item_type: item.item_type || "Required",
        has_size: item.has_size || false,
        has_brands: item.has_brands || false,
        description: item.description || "",
        is_active: item.is_active === 1,
      });
    } else if (visible) {
      // Create mode - reset form with defaults
      form.resetFields();
      form.setFieldsValue({
        category: defaultCategory?.id,
        item_type: "Required",
        has_size: false,
        has_brands: false,
        is_active: true,
      });
    }
  }, [visible, item, defaultCategory, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const normalizedName = values.name?.trim() || "";
      const shouldKeepExistingCode =
        item && normalizedName === (item.name || "").trim();
      const generatedCode = generateCodeFromName(normalizedName);
      const finalCode =
        shouldKeepExistingCode && item?.code
          ? item.code
          : generatedCode || item?.code || "";

      const payload = {
        category: values.category,
        name: values.name,
        code: finalCode,
        item_type: values.item_type,
        has_size: values.has_size,
        has_brands: values.has_brands,
        description: values.description || null,
        is_active: values.is_active ? 1 : 0,
      };

      const method = item ? "PUT" : "POST";
      const url = item
        ? domainSettings.urls.bot.itemDetail.replace(":id", item.id)
        : domainSettings.urls.bot.items;

      const response = await Utilities.fetchWithAuth(
        method,
        url,
        payload,
        navigate,
      );

      if (response.ok) {
        message.success(
          item ? "Item updated successfully" : "Item created successfully",
        );
        form.resetFields();
        onSuccess();
      } else {
        Utilities.showError(response, "Failed to save item");
      }
    } catch (error) {
      message.error("An error occurred");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={item ? "Edit Item" : "Create Item"}
      open={visible}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={700}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          item_type: "Required",
          has_size: false,
          has_brands: false,
          is_active: true,
        }}
      >
        <Form.Item
          label="Category"
          name="category"
          rules={[{ required: true, message: "Please select a category" }]}
        >
          <Select placeholder="Select category">
            {categories.map((cat) => (
              <Option key={cat.id} value={cat.id}>
                {cat.name} ({cat.code})
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Item Name"
          name="name"
          rules={[
            { required: true, message: "Please enter item name" },
            { max: 200, message: "Name cannot exceed 200 characters" },
          ]}
        >
          <Input placeholder="e.g., School Shirt, Mathematics Textbook" />
        </Form.Item>

        <Form.Item
          label="Item Type"
          name="item_type"
          rules={[{ required: true, message: "Please select item type" }]}
        >
          <Select placeholder="Select type">
            <Option value="Required">Required</Option>
            <Option value="Optional">Optional</Option>
          </Select>
        </Form.Item>

        <Form.Item label="Item Properties">
          <Form.Item name="has_size" valuePropName="checked" noStyle>
            <Checkbox>This item has size variants (e.g., S, M, L, XL)</Checkbox>
          </Form.Item>
          <br />
          <Form.Item name="has_brands" valuePropName="checked" noStyle>
            <Checkbox>This item has multiple brands/suppliers</Checkbox>
          </Form.Item>
        </Form.Item>

        <Form.Item label="Description" name="description">
          <TextArea rows={3} placeholder="Optional description of this item" />
        </Form.Item>

        <Form.Item label="Active" name="is_active" valuePropName="checked">
          <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ItemModal;
